import { useCallback, useEffect, useRef } from 'react';
import {
  createStoreHooks,
  type FeatureDeps,
  findRectangleAtPoint,
  isPointInRectangle,
  type Rectangle2D,
} from '../../../../../canvas/core';
import { useCursorTracking } from '../../../../../canvas/features/cursor-tracking';
import {
  getPageCoordinateTransformContext,
  pageCoordinates,
} from '../../../../core';
import type { PageLayoutData } from '../../../../core';
import type { createRotationFeature } from '../../../page-rotation/feature';
import { createTranslationLayerFeature } from '../../feature';

type UseActiveTranslationDeps = FeatureDeps<
  [typeof createTranslationLayerFeature],
  [typeof createRotationFeature]
>;

const { useStore, useStoreApi } = createStoreHooks<UseActiveTranslationDeps>();

const HOVER_INTENT_DELAY = 0;
const EXIT_DELAY = 90;

export const useActiveTranslation = (
  pageLayout: PageLayoutData,
  viewModels: Array<Rectangle2D & { id: string }>,
  activeRectId: string | null
) => {
  const actions = useStore(state => state.translationLayer.actions);
  const storeApi = useStoreApi();
  const { setActiveRect, clearActiveRect } = actions;
  const hoverIntentTimer = useRef<number>();
  const exitTimer = useRef<number>();

  const clearExitTimer = useCallback(() => {
    if (exitTimer.current) {
      clearTimeout(exitTimer.current);
      exitTimer.current = undefined;
    }
  }, []);

  const scheduleExit = useCallback(() => {
    if (activeRectId !== null) {
      exitTimer.current = window.setTimeout(clearActiveRect, EXIT_DELAY);
    }
  }, [activeRectId, clearActiveRect]);

  const activateRect = useCallback(
    (rectId: string) => {
      if (hoverIntentTimer.current) {
        clearTimeout(hoverIntentTimer.current);
      }
      if (rectId !== activeRectId) {
        hoverIntentTimer.current = window.setTimeout(
          () => setActiveRect(pageLayout.pageId, rectId),
          HOVER_INTENT_DELAY
        );
      }
    },
    [activeRectId, pageLayout.pageId, setActiveRect]
  );

  // Clean up pending timers on unmount to prevent writes to a stale store.
  useEffect(() => {
    return () => {
      clearTimeout(hoverIntentTimer.current);
      clearTimeout(exitTimer.current);
    };
  }, []);

  useCursorTracking(contentPoint => {
    clearExitTimer();

    if (!contentPoint || !isPointInRectangle(contentPoint, pageLayout)) {
      scheduleExit();
      return;
    }

    const pageCtx = getPageCoordinateTransformContext(
      storeApi.getState(),
      pageLayout
    );
    const pagePoint =
      pageCoordinates.point.contentToPage(pageCtx)(contentPoint);
    const hit = findRectangleAtPoint(pagePoint, viewModels);

    if (hit) {
      activateRect(hit.id);
    } else {
      scheduleExit();
    }
  });
};
